import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { throwError, of } from 'rxjs';
import { CollaboratorsService } from '../../core/collaborators/collaborators.service';
import {
  Collaborator,
  CollaboratorInviteStatus,
  CollaboratorPermission,
  CollaboratorStateModel,
  CollaboratorDetails
} from './collaborator.model';

export class FetchCollaborators {
  static readonly type = '[Collaborator] Fetch Collaborators';
  constructor(public forceRefresh: boolean = false) {}
}

export class FetchCollaboratorDetails {
  static readonly type = '[Collaborator] Fetch Collaborator Details';
  constructor(public collaboratorId: string) {}
}

export class CreateCollaborator {
  static readonly type = '[Collaborator] Create Collaborator';
  constructor(public payload: any) {}
}

export class UpdateCollaborator {
  static readonly type = '[Collaborator] Update Collaborator';
  constructor(public payload: any) {}
}

export class RemoveCollaborator {
  static readonly type = '[Collaborator] Remove Collaborator';
  constructor(public collaboratorId: string) {}
}

export class ResendInvitation {
  static readonly type = '[Collaborator] Resend Invitation';
  constructor(public collaboratorId: string) {}
}

const defaults: CollaboratorStateModel = {
  collaborators: [],
  collaboratorPermissions: {},
  selectedCollaboratorDetails: null,
  loading: false,
  error: null,
  dataLoaded: false
};

@State<CollaboratorStateModel>({
  name: 'collaborator',
  defaults
})
@Injectable()
export class CollaboratorState {
  constructor(private collaboratorsService: CollaboratorsService) {}

  @Selector()
  static collaborators(state: CollaboratorStateModel): Collaborator[] {
    return state?.collaborators || [];
  }

  @Selector()
  static collaboratorPermissions(
    state: CollaboratorStateModel
  ): Record<string, CollaboratorPermission[]> {
    return state?.collaboratorPermissions || {};
  }

  @Selector()
  static selectedCollaboratorDetails(state: CollaboratorStateModel): CollaboratorDetails | null {
    return state?.selectedCollaboratorDetails;
  }

  @Selector()
  static loading(state: CollaboratorStateModel): boolean {
    return state?.loading || false;
  }

  @Selector()
  static dataLoaded(state: CollaboratorStateModel): boolean {
    return state?.dataLoaded || false;
  }

  @Action(FetchCollaborators)
  fetchCollaborators(ctx: StateContext<CollaboratorStateModel>, action: FetchCollaborators) {
    const state = ctx.getState();

    if (state.dataLoaded && !action.forceRefresh) {
      return of(state.collaborators);
    }

    ctx.patchState({ loading: true, error: null });

    return this.collaboratorsService.getCollaborators().pipe(
      tap(response => {
        ctx.patchState({
          collaborators: response.data || [],
          loading: false,
          dataLoaded: true
        });
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to fetch collaborators',
          loading: false
        });
        return throwError(() => error);
      })
    );
  }

  @Action(FetchCollaboratorDetails)
  fetchCollaboratorDetails(
    ctx: StateContext<CollaboratorStateModel>,
    action: FetchCollaboratorDetails
  ) {
    ctx.patchState({ loading: true, error: null });
    const state = ctx.getState();

    return this.collaboratorsService.getCollaboratorDetails(action.collaboratorId).pipe(
      tap(response => {
        if (response.data) {
          ctx.patchState({
            selectedCollaboratorDetails: response.data,
            loading: false
          });

          if (response.data.shops) {
            const permissions = response.data.shops;
            const updatedPermissions = {
              ...state.collaboratorPermissions,
              [action.collaboratorId]: permissions
            };

            ctx.patchState({
              collaboratorPermissions: updatedPermissions
            });
          }
        } else {
          ctx.patchState({ loading: false });
        }
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to fetch collaborator details',
          loading: false
        });
        return throwError(() => error);
      })
    );
  }

  @Action(CreateCollaborator)
  createCollaborator(ctx: StateContext<CollaboratorStateModel>, action: CreateCollaborator) {
    ctx.patchState({ loading: true, error: null });

    return this.collaboratorsService.createCollaborator(action.payload).pipe(
      tap(response => {
        if (response.success) {
          const createdCollaborator: Collaborator = {
            id: response.data.collaboratorId || '',
            name: action.payload.name,
            email: action.payload.email,
            status: CollaboratorInviteStatus.RequestSent
          };

          const currentCollaborators = ctx.getState().collaborators || [];

          ctx.patchState({
            collaborators: [...currentCollaborators, createdCollaborator],
            loading: false
          });

          this.collaboratorsService.getCollaborators().subscribe(res => {
            ctx.patchState({
              collaborators: res.data || []
            });
          });
        } else {
          ctx.patchState({
            error: response.message || 'Failed to create collaborator',
            loading: false
          });
        }
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to create collaborator',
          loading: false
        });
        return throwError(() => error);
      })
    );
  }

  @Action(UpdateCollaborator)
  updateCollaborator(ctx: StateContext<CollaboratorStateModel>, action: UpdateCollaborator) {
    ctx.patchState({ loading: true, error: null });
    const state = ctx.getState();

    return this.collaboratorsService.updateCollaborator(action.payload).pipe(
      tap(response => {
        if (response.success) {
          const collaboratorId = action.payload.collaboratorId;
          const currentCollaborators = state.collaborators || [];
          const updatedCollaborators = currentCollaborators.map(c =>
            c.id === collaboratorId ? { ...c, name: action.payload.name } : c
          );

          ctx.patchState({
            collaborators: updatedCollaborators,
            loading: false
          });

          this.collaboratorsService.getCollaborators().subscribe(res => {
            ctx.patchState({
              collaborators: res.data || []
            });
          });
        } else {
          ctx.patchState({
            error: response.message || 'Failed to update collaborator',
            loading: false
          });
        }
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to update collaborator',
          loading: false
        });
        return throwError(() => error);
      })
    );
  }

  @Action(RemoveCollaborator)
  removeCollaborator(ctx: StateContext<CollaboratorStateModel>, action: RemoveCollaborator) {
    ctx.patchState({ loading: true, error: null });
    const state = ctx.getState();

    return this.collaboratorsService.removeCollaborator(action.collaboratorId).pipe(
      tap(response => {
        if (response.success) {
          const currentCollaborators = state.collaborators || [];
          const updatedCollaborators = currentCollaborators.filter(
            c => c.id !== action.collaboratorId
          );

          const updatedPermissions = { ...(state.collaboratorPermissions || {}) };

          if (updatedPermissions[action.collaboratorId]) {
            delete updatedPermissions[action.collaboratorId];
          }

          ctx.patchState({
            collaborators: updatedCollaborators,
            collaboratorPermissions: updatedPermissions,
            loading: false
          });
        } else {
          ctx.patchState({
            error: response.message || 'Failed to remove collaborator',
            loading: false
          });
        }
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to remove collaborator',
          loading: false
        });
        return throwError(() => error);
      })
    );
  }

  @Action(ResendInvitation)
  resendInvitation(ctx: StateContext<CollaboratorStateModel>, action: ResendInvitation) {
    ctx.patchState({ loading: true, error: null });

    return this.collaboratorsService
      .resendInvitationLink({ collaboratorId: action.collaboratorId })
      .pipe(
        tap(() => {
          ctx.patchState({ loading: false });
        }),
        catchError(error => {
          ctx.patchState({
            error: error.message || 'Failed to resend invitation',
            loading: false
          });
          return throwError(() => error);
        })
      );
  }
}
